import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getProductById, resetProduct, addToCart } from '../features/product/productSlice';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import AddToCartButton from '../components/AddToCartButton';
import ProductGallery from '../components/ProductGallery';
import { FaArrowLeft, FaMinus, FaPlus } from 'react-icons/fa';
import { formatPrice, resolveCurrency } from '../utils/currency';
import { analyticsItemFromCartItem, centsToAnalyticsAmount, trackEvent } from '../utils/analytics';

function Product() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { product, isLoading, isError, message } = useSelector((state) => state.product);
  
  // Local state for product interaction
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [activeImage, setActiveImage] = useState(null);
  const [waitingForDeepLink, setWaitingForDeepLink] = useState(true);
  const deepLinkArtworkRef = useRef(null);
  const deepLinkHandledRef = useRef(false);
  const deepLinkInvalidRef = useRef(false);
  const market = sessionStorage.getItem('liviiart_market');

  const hasOptions =
    product?.productType === 'variable' &&
    Array.isArray(product?.options) &&
    product.options.length > 0;

  useEffect(() => {
    if (id) {
      dispatch(getProductById(id));
    }
    
    return () => {
      dispatch(resetProduct());
    };
  }, [id, dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const artworkParam = params.get('artwork');
    deepLinkArtworkRef.current = artworkParam;
    deepLinkHandledRef.current = false;
    deepLinkInvalidRef.current = false;
  }, [location.search]);

  useEffect(() => {
    if (!product) return;
  
    // Don't override selections before deep-linking happens
    if (waitingForDeepLink) return;
  
    if (product.productType === 'variable' && product.options?.length) {
      const defaults = {};
      product.options.forEach((option) => {
        const uniqueValues = [...new Set(option.values || [])];
        if (uniqueValues.length) {
          defaults[option.name] = uniqueValues[0];
        }
      });
      setSelectedOptions(defaults);
      setSelectedVariant(null);
    } else {
      setSelectedOptions({});
      setSelectedVariant(product.variants?.[0] || null);
    }
  }, [product, waitingForDeepLink]);

  useEffect(() => {
    if (!product) return;
    if (!hasOptions) {
      setWaitingForDeepLink(false);
      return;
    }
  
    const desiredArtwork = deepLinkArtworkRef.current;
    if (!desiredArtwork || deepLinkHandledRef.current) {
      setWaitingForDeepLink(false);
      return;
    }
  
    const artworkOption = product.options.find(
      (option) => option.name?.toLowerCase() === "artwork"
    );
    if (!artworkOption) {
      setWaitingForDeepLink(false);
      return;
    }
  
    const match = artworkOption.values?.find(
      (value) => value?.toLowerCase() === desiredArtwork.toLowerCase()
    );
  
    if (!match) {
      toast.info(`Couldn't find a "${desiredArtwork}" print option.`);
      deepLinkInvalidRef.current = true;
      setWaitingForDeepLink(false);
      return;
    }
  
    setSelectedOptions((prev) => ({
      ...prev,
      [artworkOption.name]: match,
    }));
  
    deepLinkHandledRef.current = true;
    setWaitingForDeepLink(false);
  }, [product, hasOptions]);  

  useEffect(() => {
    if (!product) return;
    const initialImage = product.image || (product.images && product.images[0]) || null;
    
    const isPrintProduct = product?.category?.toLowerCase() === 'print';
    if (!isPrintProduct) {
      setActiveImage(product?.variants[0]?.image)
    } else {
      setActiveImage(initialImage);
    }
  }, [product]);

  useEffect(() => {
    if (waitingForDeepLink) return; 
    if (!product || product.productType !== 'variable') {
      return;
    }

    if (!product.options || product.options.length === 0) {
      setSelectedVariant(product.variants?.[0] || null);
      return;
    }

    const allOptionsChosen =
      Object.keys(selectedOptions).length === product.options.length &&
      product.options.every((option) => selectedOptions[option.name]);

    if (!allOptionsChosen) {
      setSelectedVariant(null);
      return;
    }

    const matchingVariant = product.variants?.find((variant) => {
      if (!variant.optionValues) return false;
      return variant.optionValues.every(
        (opt) => selectedOptions[opt.name] === opt.value
      );
    });

    setSelectedVariant(matchingVariant || null);
  }, [product, selectedOptions]);

  useEffect(() => {
    if (!product || !selectedVariant) return;

    const fallbackImage =
      resolveImageSrc(product.image) ||
      (product.images && resolveImageSrc(product.images[0])) ||
      null;
    setActiveImage(selectedVariant.image || fallbackImage);
    setQuantity(1);
  }, [selectedVariant, product]);

  useEffect(() => {
    if (isError) {
      toast.error(message);
      navigate('/shop');
    }
  }, [isError, message, navigate]);

  const optionList = useMemo(() => {
    if (!hasOptions) return [];
    return product.options.map((option) => ({
      ...option,
      values: Array.from(new Set(option.values || [])),
    }));
  }, [hasOptions, product]);

  const findValueAvailability = useCallback(
    (optionName, optionValue, selectionOverride = null) => {
      if (!product?.variants?.length) return false;
      const selection = selectionOverride || selectedOptions;
      return product.variants.some((variant) => {
        if (!variant.optionValues) return false;
        const matchesCandidate = variant.optionValues.some(
          (opt) => opt.name === optionName && opt.value === optionValue
        );
        if (!matchesCandidate) return false;

        return variant.optionValues.every((opt) => {
          if (opt.name === optionName) return true;
          const selected = selection[opt.name];
          return !selected || selected === opt.value;
        });
      });
    },
    [product?.variants, selectedOptions]
  );

  const reflowSelection = useCallback(
    (baseSelection, startIndex = 0) => {
      let nextSelection = { ...baseSelection };
      if (!Array.isArray(optionList) || optionList.length === 0) {
        return nextSelection;
      }

      for (let idx = startIndex + 1; idx < optionList.length; idx += 1) {
        const option = optionList[idx];
        if (!option?.name) continue;
        const currentValue = nextSelection[option.name];
        const availableValues = option.values.filter((value) => {
          const scoped = { ...nextSelection, [option.name]: value };
          return findValueAvailability(option.name, value, scoped);
        });

        if (availableValues.length === 0) {
          if (currentValue) {
            const scoped = { ...nextSelection };
            delete scoped[option.name];
            nextSelection = scoped;
          }
          continue;
        }

        if (!currentValue || !availableValues.includes(currentValue)) {
          nextSelection = { ...nextSelection, [option.name]: availableValues[0] };
        }
      }

      return nextSelection;
    },
    [optionList, findValueAvailability]
  );

  useEffect(() => {
    if (waitingForDeepLink) return; 
    if (!hasOptions || !product?.variants?.length) return;

    let updated = false;
    let workingSelection = { ...selectedOptions };

    optionList.forEach((option) => {
      const currentValue = workingSelection[option.name];
      const currentValid =
        currentValue &&
        findValueAvailability(option.name, currentValue, workingSelection);

      if (!currentValid) {
        const fallback = option.values.find((value) => {
          const scopedSelection = { ...workingSelection, [option.name]: value };
          return findValueAvailability(option.name, value, scopedSelection);
        });

        if (fallback) {
          workingSelection = { ...workingSelection, [option.name]: fallback };
          updated = true;
        } else if (currentValue) {
          const nextSelection = { ...workingSelection };
          delete nextSelection[option.name];
          workingSelection = nextSelection;
          updated = true;
        }
      }
    });

    if (updated) {
      setSelectedOptions(workingSelection);
    }
  }, [hasOptions, optionList, product?.variants, selectedOptions, findValueAvailability]);

  const handleOptionSelect = (optionName, optionValue, optionIndex = 0) => {
    setSelectedOptions((prev) => {
      let base = { ...prev, [optionName]: optionValue };

      if (optionIndex === 0 && Array.isArray(optionList)) {
        for (let idx = optionIndex + 1; idx < optionList.length; idx += 1) {
          const nextOptionName = optionList[idx]?.name;
          if (nextOptionName) {
            delete base[nextOptionName];
          }
        }
      }

      return reflowSelection(base, optionIndex);
    });
  };

  const handleAddToCart = () => {
    if (!selectedVariant) {
      toast.error('Please select an available combination.');
      return;
    }

    if (isOutOfStock()) {
      toast.error('This variant is currently out of stock.');
      return;
    }

    const cartItem = {
      // You are already building the item perfectly
      _id: product._id, // Ensure main product ID is included
      title: product.title,
      image: product.image, // Main product image for cart display
      category: product.category,
      countries: Array.isArray(product.countries)
        ? product.countries.map((country) => country?.toLowerCase?.() ?? country)
        : [],
      variant: selectedVariant,
      quantity
    };

    // Uncomment this line:
    dispatch(addToCart(cartItem));
    trackEvent('add_to_cart', {
      currency: resolveCurrency({ market }).toUpperCase(),
      value: centsToAnalyticsAmount((selectedVariant.discount_price || selectedVariant.price) * quantity),
      items: [analyticsItemFromCartItem(cartItem)],
    });
    toast.success('Product added to cart!');
  };

  const handleQuantityChange = (action) => {
    if (!selectedVariant) return;

    if (action === 'increase') {
      if (quantity < (selectedVariant?.countInStock || 1)) {
        setQuantity(quantity + 1);
      }
    } else if (action === 'decrease') {
      if (quantity > 1) {
        setQuantity(quantity - 1);
      }
    }
  };

  const getPrice = () => {
    if (!selectedVariant) return '0.00';
    const price = selectedVariant.discount_price || selectedVariant.price;
    return formatPrice(price, { market });
  };

  const getOriginalPrice = () => {
    if (!selectedVariant || !selectedVariant.discount_price) return null;
    return formatPrice(selectedVariant.price, { market });
  };

  const isOutOfStock = () => {
    if (!selectedVariant) return false;
    return selectedVariant.countInStock < 1;
  };

  const hasDiscount = () => {
    return selectedVariant && selectedVariant.discount_price;
  };

  const resolveImageSrc = (image) => {
    if (!image) return null;
    if (typeof image === 'string') return image;
    if (typeof image === 'object' && image.src) return image.src;
    return null;
  };

  const getImageDimensions = (image) => {
    if (!image || typeof image !== 'object') return null;
    const width = Number(image.width ?? image.image?.width);
    const height = Number(image.height ?? image.image?.height);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return null;
    }
    return { width, height };
  };

  const getOrientationClass = (image) => {
    const dims = getImageDimensions(image);
    if (!dims) return 'product-main-image-wrapper--square';
    const ratio = dims.width / dims.height;
    if (ratio >= 1.25) return 'product-main-image-wrapper--landscape';
    if (ratio <= 0.8) return 'product-main-image-wrapper--portrait';
    return 'product-main-image-wrapper--square';
  };

  const mainImageOrientation = useMemo(() => getOrientationClass(activeImage), [activeImage]);

  const galleryImages = useMemo(() => {
    const list = Array.isArray(product?.images) ? [...product.images] : [];
    const isPrintProduct = product?.category?.toLowerCase() === 'print';
    if (isPrintProduct) {
      return list;
    }
    const mainImageObj = product?.image;
    const mainSrc = resolveImageSrc(mainImageObj);
    if (mainSrc) {
      const alreadyContainsMain = list.some((img) => resolveImageSrc(img) === mainSrc);
      if (!alreadyContainsMain) {
        list.unshift(mainImageObj);
      }
    }
    return list;
  }, [product]);

  if (isLoading) {
    return <Loading />;
  }

  if (!product) {
    return (
      <div className="container">
        <div className="error-message">
          <p>Product not found</p>
          <button className="btn btn-primary" onClick={() => navigate('/shop')}>
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-page">
        <div className="px-20">
            <button className="btn btn-back" onClick={() => navigate('/shop')}>
                <FaArrowLeft /> Back to shop
            </button>
        </div>
       
        <div className="product-details">
          <div className="product-media">
            {/* Product Images */} 
            <div className="product-images">
            <div className={`product-main-image-wrapper ${mainImageOrientation}`}>
                <img 
                    src={
                      resolveImageSrc(activeImage) ||
                      product.image?.src ||
                      '/placeholder-image.jpg'
                    } 
                    alt={product.title}
                    className="product-main-image"
                />
                {isOutOfStock() && (
                    <div className="out-of-stock-overlay">Out of Stock</div>
                )}
                {(!isOutOfStock() && hasDiscount()) && (
                    <div className="discount-badge">Sale</div>
                )}
            </div>

            {galleryImages.length > 1 && (
                <ProductGallery
                    images={galleryImages}
                    onSelect={(img) => setActiveImage(img)}
                />
            )}


            </div>
          </div>

          {/* Product Info */}
          <div className="product-info">
            <h2 className="product-title">{product.title}</h2>

            {/* Product price */}
            <div className="product-price">
              {hasDiscount() && (
                <span className="original-price">{getOriginalPrice()}</span>
              )}
              <span className="current-price">{getPrice()}</span>
            </div>

            {hasOptions && (
              <div className="product-options">
                {optionList.map((option, optionIndex) => (
                  <div key={option.name} className="product-option">
                    <div className="product-option__label">
                      <span>{option.name}</span>
                      {selectedOptions[option.name] && (
                        <span className="product-option__value">
                          {selectedOptions[option.name]}
                        </span>
                      )}
                    </div>
                    <div className="product-option__values">
                      {(optionIndex === 0
                        ? option.values
                        : option.values.filter((value) =>
                            findValueAvailability(option.name, value)
                          )
                      ).map((value) => {
                        const isSelected =
                          selectedOptions[option.name] === value;
                        const isFirstOption = optionIndex === 0;
                        const isAvailable = isFirstOption
                          ? true
                          : findValueAvailability(option.name, value);
                        return (
                          <button
                            type="button"
                            key={value}
                            className={`option-pill ${
                              isSelected ? 'is-selected' : ''
                            } ${!isAvailable && !isFirstOption ? 'is-disabled' : ''}`}
                            onClick={() =>
                              (isAvailable || isFirstOption) &&
                              handleOptionSelect(option.name, value, optionIndex)
                            }
                            disabled={!isAvailable && !isFirstOption}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="variant-meta">
              {hasOptions && !selectedVariant && (
                <p className="variant-warning">
                  Choose each option to see availability.
                </p>
              )}
              {selectedVariant && selectedVariant.optionValues?.length > 0 && (
                <p className="variant-summary">
                  Selected:{' '}
                  {selectedVariant.optionValues
                    .map((opt) => `${opt.name}: ${opt.value}`)
                    .join(' · ')}
                </p>
              )}
              {/* {selectedVariant && (
                <p
                  className={`stock-indicator ${
                    isOutOfStock() ? 'stock-indicator__out' : ''
                  }`}
                >
                  {isOutOfStock()
                    ? 'Out of stock'
                    : `${selectedVariant.countInStock} in stock`}
                </p>
              )} */}
            </div>

            {/* Product Description */}
            {product.description && (
              <div className="product-description pt-4 pb-4">
                <div 
                  className="description-content"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              </div>
            )}

            <div className="product-purchase-row">
              <div className="quantity-selector">
                <button
                  type="button"
                  className="quantity-btn"
                  onClick={() => handleQuantityChange('decrease')}
                  disabled={quantity <= 1}
                >
                  <FaMinus />
                </button>
                <span className="quantity-value">{quantity}</span>
                <button
                  type="button"
                  className="quantity-btn"
                  onClick={() => handleQuantityChange('increase')}
                  disabled={
                    !selectedVariant ||
                    quantity >= (selectedVariant?.countInStock || 1)
                  }
                >
                  <FaPlus />
                </button>
              </div>

              <AddToCartButton
                onAddToCart={handleAddToCart}
                isOutOfStock={isOutOfStock()}
                disabled={hasOptions && !selectedVariant}
                className="product-add-to-cart"
              >
                Add to cart
              </AddToCartButton>
            </div>
          </div>
        </div>
      </div>
  );
}

export default Product;
